import { __ } from "@wordpress/i18n";
import {
	useBlockProps,
	InspectorControls,
	useSettings,
} from "@wordpress/block-editor";
import {
	Panel,
	PanelBody,
	RangeControl,
	SelectControl,
	ColorPalette,
	DateTimePicker,
	__experimentalBoxControl as BoxControl,
	__experimentalBorderControl as BorderControl,
	__experimentalUnitControl as UnitControl,
} from "@wordpress/components";
import { useEffect, useRef } from "@wordpress/element";

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import "./editor.scss";

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function Edit(props) {
	const { attributes, setAttributes } = props;
	const countDownRef = useRef(null);

	const fontFamilies = useSettings("typography.fontFamilies");

	// Retrieve the theme's default colors from theme.json
	const colorSettings = useSettings("color.palette");
	const themeColors = colorSettings[0];

	const fontOptions =
		fontFamilies && fontFamilies[0].theme
			? fontFamilies[0].theme.map((font) => ({
					label: font.name,
					value: font.name,
			  }))
			: [];

	function setOption(key, value) {
		setAttributes({ [key]: value });
	}

	function setMetaOption(key, value) {
		setAttributes({
			options: {
				...attributes.options,
				[key]: value,
			},
		});
	}

	useEffect(() => {
		if (typeof wp === "undefined" || !wp.domReady) return; // Ensure WP environment

		const blockElement = countDownRef.current;
		if (!blockElement) return;

		const countDownDate = new Date(attributes.countDownDate).getTime();
		if (!countDownDate) return;

		// Cache DOM elements
		const daysEl = blockElement.querySelector(".days");
		const hoursEl = blockElement.querySelector(".hours");
		const minutesEl = blockElement.querySelector(".minutes");
		const secondsEl = blockElement.querySelector(".seconds");
		const expiredEl = blockElement.querySelector(".expired");
		const countDownEl = blockElement.querySelector(".wpopus-count-down");

		if (!daysEl || !hoursEl || !minutesEl || !secondsEl || !expiredEl) return;

		const updateCountdown = () => {
			const now = Date.now();
			const distance = countDownDate - now;

			if (distance < 0) {
				expiredEl.textContent = __("Expired", "wpopus");

				// Add/remove classes
				countDownEl.classList.add("is-expired");
				expiredEl.classList.add("is-visible");

				clearInterval(intervalId);
				return;
			} else {
				countDownEl.classList.remove("is-expired");
				expiredEl.classList.remove("is-visible");
			}

			// Calculate time units
			const days = Math.floor(distance / (1000 * 60 * 60 * 24));
			const hours = Math.floor(
				(distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
			);
			const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
			const seconds = Math.floor((distance % (1000 * 60)) / 1000);

			// Update DOM
			daysEl.textContent = days;
			hoursEl.textContent = hours.toString().padStart(2, "0");
			minutesEl.textContent = minutes.toString().padStart(2, "0");
			secondsEl.textContent = seconds.toString().padStart(2, "0");
		};

		const intervalId = setInterval(updateCountdown, 500);

		// Cleanup on unmount
		return () => clearInterval(intervalId);
	}, [attributes.countDownDate, countDownRef.current]);

	const countDownStyle = {
		"--wpopus-count-down-width": `${attributes.countDownWidth}px`,
		"--wpopus-count-down-height": `${attributes.countDownHeight}px`,
		"--wpopus-count-down-border": `${attributes.countDownBorder.width} ${attributes.countDownBorder.style} ${attributes.countDownBorder.color}`,
		"--wpopus-count-down-border-radius": `${attributes.countDownBorderRadius}px`,
		"--wpopus-count-down-bg-color": `${attributes.countDownBgColor}`,
		"--wpopus-count-down-number-font": `${attributes.options.numberFontFamily}`,
		"--wpopus-count-down-number-font-size": `${attributes.options.numberFontSize}`,
		"--wpopus-count-down-number-font-weight": `${attributes.options.numberFontWeight}`,
		"--wpopus-count-down-number-font-color": `${attributes.options.numberFontColor}`,
		"--wpopus-count-down-label-font": `${attributes.options.labelFontFamily}`,
		"--wpopus-count-down-label-font-size": `${attributes.options.labelFontSize}`,
		"--wpopus-count-down-label-font-weight": `${attributes.options.labelFontWeight}`,
		"--wpopus-count-down-label-font-color": `${attributes.options.labelFontColor}`,
	};

	return (
		<div {...useBlockProps()}>
			<InspectorControls key="setting">
				<Panel className={"wpopus-count-down"}>
					<PanelBody
						title={__("Count Down Time Settings", "wpopus")}
						initialOpen={true}
					>
						<fieldset>
							<DateTimePicker
								currentDate={attributes.countDownDate}
								onChange={(newValue) => {
									setOption("countDownDate", newValue);
								}}
								is12Hour={false}
							/>
						</fieldset>
					</PanelBody>
				</Panel>
			</InspectorControls>

			<InspectorControls key="style" group="styles">
				<Panel>
					<PanelBody title={__("Segment Style", "wpopus")} initialOpen={false}>
						<fieldset>
							<RangeControl
								__nextHasNoMarginBottom
								__next40pxDefaultSize
								label={__("Height (px)", "wpopus")}
								value={attributes.countDownHeight}
								onChange={(newValue) => {
									setOption("countDownHeight", parseInt(newValue));
								}}
								min={50}
								max={300}
							/>
						</fieldset>

						<br />
						<fieldset>
							<RangeControl
								__nextHasNoMarginBottom
								__next40pxDefaultSize
								label={__("Width (px)", "wpopus")}
								value={attributes.countDownWidth}
								onChange={(newValue) => {
									setOption("countDownWidth", parseInt(newValue));
								}}
								min={50}
								max={300}
							/>
						</fieldset>

						<br />
						<fieldset>
							<label className="inspector-control-label">
								{__("Background Color", "wpopus")}
								<ColorPalette
									className="wpopus-color-palette"
									colors={themeColors}
									clearable={true}
									enableAlpha={true}
									value={attributes.countDownBgColor}
									onChange={(newValue) => {
										setOption("countDownBgColor", newValue);
									}}
								/>
							</label>
						</fieldset>

						<br />
						<fieldset>
							<BorderControl
								label={__("Border", "wpopus")}
								value={attributes.countDownBorder}
								disableUnits={true}
								enableStyle={false}
								onChange={(newValue) => {
									setOption("countDownBorder", newValue);
								}}
							/>
						</fieldset>

						<br />
						<fieldset>
							<RangeControl
								__nextHasNoMarginBottom
								__next40pxDefaultSize
								label={__("Border Radius", "wpopus")}
								value={attributes.countDownBorderRadius}
								onChange={(newValue) => {
									setOption("countDownBorderRadius", parseInt(newValue));
								}}
								min={0}
								max={300}
							/>
						</fieldset>
					</PanelBody>

					<PanelBody title={__("Number Style", "wpopus")} initialOpen={false}>
						<br />
						<fieldset>
							<SelectControl
								__nextHasNoMarginBottom={true}
								label={__("Font Family", "wpopus")}
								labelPosition={"top"}
								value={attributes.options.numberFontFamily}
								options={fontOptions}
								onChange={(newValue) => {
									setMetaOption("numberFontFamily", newValue);
								}}
							/>
						</fieldset>

						<br />
						<fieldset>
							<UnitControl
								label={__("Font Size", "wpopus")}
								value={attributes.options.numberFontSize}
								min={0}
								step={0.1}
								units={[
									{
										value: "px",
										label: "px",
										default: attributes.options.numberFontSize,
									},
									{ value: "em", label: "em", default: 0 },
									{ value: "rem", label: "rem", default: 0 },
								]}
								onChange={(newValue) => {
									setMetaOption("numberFontSize", newValue);
								}}
							/>
						</fieldset>

						<br />
						<fieldset>
							<SelectControl
								__nextHasNoMarginBottom={true}
								label={__("Font Weight", "wpopus")}
								labelPosition={"top"}
								value={attributes.options.numberFontWeight}
								options={[
									{ label: __("Thin", "wpopus"), value: "100" },
									{
										label: __("Extra Light", "wpopus"),
										value: "200",
									},
									{
										label: __("Light", "wpopus"),
										value: "300",
									},
									{
										label: __("Regular", "wpopus"),
										value: "400",
									},
									{
										label: __("Medium", "wpopus"),
										value: "500",
									},
									{
										label: __("Semi Bold", "wpopus"),
										value: "600",
									},
									{ label: __("Bold", "wpopus"), value: "700" },
									{
										label: __("Extra Bold", "wpopus"),
										value: "800",
									},
									{
										label: __("Black", "wpopus"),
										value: "900",
									},
								]}
								onChange={(newValue) => {
									setMetaOption("numberFontWeight", newValue);
								}}
							/>
						</fieldset>

						<br />
						<fieldset>
							<label className="inspector-control-label">
								{__("Text Color", "wpopus")}
								<ColorPalette
									className="wpopus-color-palette"
									colors={themeColors}
									clearable={true}
									enableAlpha={false}
									value={attributes.options.numberFontColor}
									onChange={(newValue) => {
										setMetaOption("numberFontColor", newValue);
									}}
								/>
							</label>
						</fieldset>
					</PanelBody>

					<PanelBody title={__("Label Style", "wpopus")} initialOpen={false}>
						<br />
						<fieldset>
							<SelectControl
								__nextHasNoMarginBottom={true}
								label={__("Font Family", "wpopus")}
								labelPosition={"top"}
								value={attributes.options.labelFontFamily}
								options={fontOptions}
								onChange={(newValue) => {
									setMetaOption("labelFontFamily", newValue);
								}}
							/>
						</fieldset>

						<br />
						<fieldset>
							<UnitControl
								label={__("Font Size", "wpopus")}
								value={attributes.options.labelFontSize}
								min={0}
								step={0.1}
								units={[
									{
										value: "px",
										label: "px",
										default: attributes.options.labelFontSize,
									},
									{ value: "em", label: "em", default: 0 },
									{ value: "rem", label: "rem", default: 0 },
								]}
								onChange={(newValue) => {
									setMetaOption("labelFontSize", newValue);
								}}
							/>
						</fieldset>

						<br />
						<fieldset>
							<SelectControl
								__nextHasNoMarginBottom={true}
								label={__("Font Weight", "wpopus")}
								labelPosition={"top"}
								value={attributes.options.labelFontWeight}
								options={[
									{ label: __("Thin", "wpopus"), value: "100" },
									{
										label: __("Extra Light", "wpopus"),
										value: "200",
									},
									{
										label: __("Light", "wpopus"),
										value: "300",
									},
									{
										label: __("Regular", "wpopus"),
										value: "400",
									},
									{
										label: __("Medium", "wpopus"),
										value: "500",
									},
									{
										label: __("Semi Bold", "wpopus"),
										value: "600",
									},
									{ label: __("Bold", "wpopus"), value: "700" },
									{
										label: __("Extra Bold", "wpopus"),
										value: "800",
									},
									{
										label: __("Black", "wpopus"),
										value: "900",
									},
								]}
								onChange={(newValue) => {
									setMetaOption("labelFontWeight", newValue);
								}}
							/>
						</fieldset>

						<br />
						<fieldset>
							<label className="inspector-control-label">
								{__("Text Color", "wpopus")}
								<ColorPalette
									className="wpopus-color-palette"
									colors={themeColors}
									clearable={true}
									enableAlpha={false}
									value={attributes.options.labelFontColor}
									onChange={(newValue) => {
										setMetaOption("labelFontColor", newValue);
									}}
								/>
							</label>
						</fieldset>
					</PanelBody>
				</Panel>
			</InspectorControls>

			<div
				ref={countDownRef}
				className="wpopus-count-down-wrapper"
				style={{ ...countDownStyle }}
			>
				<span className="expired"></span>
				<ul className="wpopus-count-down">
					<li>
						<span className="value days"></span>
						<span className="label">{__("Days", "wpopus")}</span>
					</li>
					<li>
						<span className="value hours"></span>
						<span className="label">{__("Hours", "wpopus")}</span>
					</li>
					<li>
						<span className="value minutes"></span>
						<span className="label">{__("Minutes", "wpopus")}</span>
					</li>
					<li>
						<span className="value seconds"></span>
						<span className="label">{__("Seconds", "wpopus")}</span>
					</li>
				</ul>
			</div>
		</div>
	);
}
